import { Measure, MetricDataPoint } from 'cio/utils/metrics/common';
import { JourneyMetrics, JourneyMetricsMeasure } from 'cio/utils/journey-metrics';
import { Metric } from 'cio/utils/metrics/models/metric';
import {
  changeFromLast,
  ChangeFromLast,
  ChangeFromLastPositivity,
  trendIconClass,
  trendPositivity,
  trendTextClass,
} from 'cio/utils/metrics/trends';
import ActionModel, { VariationBadge } from 'cio/models/action';

export interface SidebarItem<T extends Measure | JourneyMetricsMeasure> {
  id: string;
  index: number;
  value: string;
  label: string;
  color: {
    name: string;
    value: string;
  };
  hasRate: boolean;
  measure: T;
  denominatorMeasure: T | null;
  action?: ActionModel;
  metric?: Metric;
  badge?: VariationBadge;
  disabled?: boolean;
  disabledTooltip?: string;
}

export interface DataContainer<T extends Measure | JourneyMetricsMeasure> {
  rateSeries: Record<T, MetricDataPoint[]>;
  totalSeries: Record<T, MetricDataPoint[]>;
  rates: Record<T, number>;
  totals: Record<T, number>;
}

export function prepareDataContainer(
  metric: JourneyMetrics,
  measures: JourneyMetricsMeasure[],
): DataContainer<JourneyMetricsMeasure>;
export function prepareDataContainer(metric: Metric, measures: Measure[]): DataContainer<Measure>;
export function prepareDataContainer(metric: any, measures: any[]): DataContainer<any> {
  type MeasureType = (typeof measures)[number];

  return {
    rateSeries: measures.reduce<Record<MeasureType, MetricDataPoint[]>>((acc, m) => {
      acc[m] = metric[m].rates ?? [];
      return acc;
    }, {} as any),
    totalSeries: measures.reduce<Record<MeasureType, MetricDataPoint[]>>((acc, m) => {
      acc[m] = metric[m].totals ?? [];
      return acc;
    }, {} as any),
    rates: measures.reduce<Record<MeasureType, number>>((acc, m) => {
      acc[m] = metric[m].rate ?? 0;
      return acc;
    }, {} as any),
    totals: measures.reduce<Record<MeasureType, number>>((acc, m) => {
      acc[m] = metric[m].total ?? 0;
      return acc;
    }, {} as any),
  } as DataContainer<MeasureType>;
}

export interface ChartSeriesItem {
  id: string;
  name: string;
  data: MetricDataPoint[];
  yAxis: number;
  colorIndex: string;
  type: string;
  custom: {
    hasRate: boolean;
    rateData: MetricDataPoint[];
    totalData: MetricDataPoint[];
    rateChangeData: (null | {
      changeFromLast: ChangeFromLast | null;
      trendPositivity: ChangeFromLastPositivity | null;
      trendIconClass: string;
      trendTextClass: string;
    })[];
    totalChangeData: (null | {
      changeFromLast: ChangeFromLast | null;
      trendPositivity: ChangeFromLastPositivity | null;
      trendIconClass: string;
      trendTextClass: string;
    })[];
    colorHexValue: string;
  };
}

export function chartChangeData(metrics: MetricDataPoint[], measure: Measure | JourneyMetricsMeasure) {
  return metrics.map((p, i) => {
    if (i === 0) {
      return null;
    }

    let change = changeFromLast([metrics[i - 1], p]);
    let positivity = trendPositivity(measure, change);

    return {
      changeFromLast: change,
      trendPositivity: positivity,
      trendIconClass: trendIconClass(positivity),
      trendTextClass: trendTextClass(positivity),
    };
  });
}
